import { useEffect, useRef, useState } from 'react';

// Captura CONSCIENTE de fecha+hora para registros que pueden ser tardíos (compra, gasto, venta). La
// fecha/hora está SIEMPRE visible (no un default oculto), con atajos Hoy/Ayer, un eco grande de lo que
// se guardará, y — si la fecha es PASADA — una casilla OBLIGATORIA "La fecha y hora son correctas".
// Reporta al padre el ISO elegido y si está listo para guardar (ready=false bloquea el guardado).
// No hay default de hora fijo (no serviría para todos los turnos): el empleado la elige y la ve.
//
// Modo `explicit` (captura de ticket guiada, FI-20): nada preseleccionado al inicio (se elige Hoy,
// Ayer u Otra fecha); ningún chip rellena la hora: con «Hoy» aparece el preset «Ahora · 9:14 p.m.» o
// se escribe; con Ayer u Otra fecha se escribe. Fecha y hora son obligatorias. Mientras no esté
// completo, reporta iso='' y ready=false. Sin `explicit` el comportamiento es exactamente el de siempre.

function toLocalParts(d: Date) {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString();
  return { date: local.slice(0, 10), time: local.slice(11, 16) };
}
const todayStr = () => toLocalParts(new Date()).date;
const shiftDays = (n: number) => toLocalParts(new Date(Date.now() - n * 86_400_000)).date;
const combineIso = (date: string, time: string) => new Date(`${date}T${time || '00:00'}`).toISOString();
const DAY_FMT = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
const TIME_FMT = new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit' });
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type DayMode = 'today' | 'yesterday' | 'other';
const modeFor = (date: string): DayMode => (date === todayStr() ? 'today' : date === shiftDays(1) ? 'yesterday' : 'other');

export function DateTimeField({ value, onChange, startAcknowledged = false, label = '¿Cuándo ocurrió?', explicit = false }: {
  value?: string | null;
  onChange: (iso: string, ready: boolean) => void;
  startAcknowledged?: boolean;
  label?: string;
  /** Sin chip elegido al inicio; ningún chip rellena la hora («Hoy» ofrece «Ahora»); ambas obligatorias. */
  explicit?: boolean;
}) {
  const init = value ? toLocalParts(new Date(value)) : explicit ? { date: '', time: '' } : toLocalParts(new Date());
  const [dateStr, setDateStr] = useState(init.date);
  const [timeStr, setTimeStr] = useState(init.time);
  const [acked, setAcked] = useState(startAcknowledged);
  // El calendario solo se despliega al elegir "Otra fecha" (Hoy/Ayer son un toque, sin campo extra).
  const [mode, setMode] = useState<DayMode | ''>(() => (init.date ? modeFor(init.date) : ''));

  const today = todayStr();
  const yesterday = shiftDays(1);
  const nowTime = toLocalParts(new Date()).time;
  const complete = Boolean(dateStr) && (!explicit || Boolean(timeStr));
  const isPast = complete && dateStr < today;
  const ready = complete && (!isPast || acked);
  const iso = complete ? combineIso(dateStr, timeStr) : '';
  const echoDate = complete ? new Date(`${dateStr}T${timeStr || '00:00'}`) : null;

  const lastRef = useRef('');
  useEffect(() => {
    const key = `${iso}|${ready}`;
    if (key !== lastRef.current) { lastRef.current = key; onChange(iso, ready); }
  }, [iso, ready, onChange]);
  useEffect(() => { if (!isPast && acked) setAcked(false); }, [isPast, acked]);

  // Re-sincroniza si el padre entrega un `value` nuevo (p. ej. al cargar un registro para editar,
  // que llega de forma asíncrona después del montaje). No pelea con la edición del usuario porque
  // solo actúa cuando el prop `value` en sí cambia.
  const valueRef = useRef(value);
  useEffect(() => {
    if (value === valueRef.current) return;
    valueRef.current = value;
    if (!value && explicit) return; // el padre solo refleja lo que este campo emitió; no borrar lo que se está capturando
    const p = value ? toLocalParts(new Date(value)) : toLocalParts(new Date());
    setDateStr(p.date); setTimeStr(p.time); setAcked(startAcknowledged); setMode(modeFor(p.date));
  }, [value, startAcknowledged, explicit]);

  // Explícito: ningún chip rellena la hora. «Hoy» deja la hora en blanco y ofrece «Ahora»; «Ayer» y
  // «Otra fecha» exigen escribirla (la de ahora casi nunca es la de ayer). Sin explicit, como siempre.
  const pickDay = (day: DayMode, date: string) => {
    setMode(day);
    setDateStr(date);
    if (explicit) { setTimeStr(''); setAcked(false); }
  };
  const pickOther = () => {
    setMode('other');
    if (explicit) { setDateStr(''); setTimeStr(''); setAcked(false); }
  };

  const hint = !mode ? 'Elige Hoy, Ayer u Otra fecha.' : !dateStr ? 'Elige la fecha y la hora.' : mode === 'today' ? 'Toca «Ahora» o escribe la hora.' : 'Ahora escribe la hora.';
  const timeInput = <input aria-label="Hora" className="dtf__time" type="time" value={timeStr} onChange={(e) => setTimeStr(e.target.value)} />;

  return (
    <div className="dtf field">
      <span>{label}</span>
      <div className="dtf__row">
        <button type="button" className={`dtf__chip ${mode === 'today' ? 'dtf__chip--active' : ''}`} onClick={() => pickDay('today', today)}>Hoy</button>
        <button type="button" className={`dtf__chip ${mode === 'yesterday' ? 'dtf__chip--active' : ''}`} onClick={() => pickDay('yesterday', yesterday)}>Ayer</button>
        <button type="button" className={`dtf__chip ${mode === 'other' ? 'dtf__chip--active' : ''}`} onClick={pickOther}>Otra fecha</button>
        {mode === 'other' && <input aria-label="Fecha" className="dtf__date" type="date" max={today} value={dateStr} onChange={(e) => { setDateStr(e.target.value); setAcked(false); }} />}
        {!explicit && timeInput}
      </div>
      {explicit && mode && (
        <div className="dtf__row dtf__row--time">
          <span className="dtf__label">Hora</span>
          {mode === 'today' && <button type="button" className={`dtf__chip ${timeStr && timeStr === nowTime ? 'dtf__chip--active' : ''}`} onClick={() => setTimeStr(nowTime)}>Ahora · {TIME_FMT.format(new Date())}</button>}
          {timeInput}
        </div>
      )}
      {echoDate
        ? <div className={`dtf__echo ${isPast ? 'dtf__echo--past' : ''}`}>Se registrará: <strong>{cap(DAY_FMT.format(echoDate))}, {TIME_FMT.format(echoDate)}</strong></div>
        : <div className="dtf__echo">{hint}</div>}
      {isPast && (
        <div className="dtf__past">
          <div className="alert alert--warning">Estás registrando con una <strong>fecha pasada</strong>. Verifica el día y la hora (deben caer dentro del turno correcto).</div>
          <label className="dtf__ack"><input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} /> <span>La fecha y hora son correctas</span></label>
        </div>
      )}
    </div>
  );
}
